<template>
  <!-- Loading -->
  <div v-if="loading" class="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div class="relative">
      <div class="animate-spin rounded-full h-20 w-20 border-4 border-slate-200"></div>
      <div class="animate-spin rounded-full h-20 w-20 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
    </div>
    <p class="mt-6 text-slate-500 font-medium">Cargando empresa...</p>
  </div>

  <!-- No encontrada -->
  <div v-else-if="!config" class="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-red-50 p-4">
    <div class="text-center max-w-md">
      <div class="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Icon icon="mdi:office-building-off" class="text-5xl text-red-500" />
      </div>
      <h1 class="text-2xl font-bold text-slate-800 mb-2">Empresa no encontrada</h1>
      <p class="text-slate-500 mb-6">El tenant <strong>"{{ tenantId }}"</strong> no existe o no está activo.</p>
      <router-link to="/login" class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all">
        <Icon icon="mdi:login" />
        Ir al sistema
      </router-link>
    </div>
  </div>

  <!-- Landing -->
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex flex-col">
    
    <!-- Navbar -->
    <nav class="sticky top-0 w-full z-40 bg-white/80 backdrop-blur-xl shadow-md border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16 md:h-20">
          
          <!-- Logo -->
          <router-link :to="`/empresa/${tenantId}`" class="flex items-center gap-3">
            <img 
              :src="logoUrl" 
              :alt="config.nombreComercial || config.nombreEmpresa" 
              class="h-10 md:h-12 w-auto object-contain rounded-xl shadow-sm border border-slate-100"
              @error="handleLogoError"
            />
            <div class="flex flex-col">
              <span class="text-base md:text-lg font-black tracking-tight text-slate-900">
                {{ config.nombreComercial || config.nombreEmpresa }}
              </span>
              <span class="text-[9px] text-slate-400 font-bold uppercase tracking-wider -mt-1">
                {{ config.slogan || 'Bienvenido' }}
              </span>
            </div>
          </router-link>

          <!-- Nav Links -->
          <div class="hidden md:flex items-center space-x-2">
            <router-link 
              :to="`/empresa/${tenantId}/catalogo`" 
              class="px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:scale-105 transition-all shadow-md"
            >
              <Icon icon="mdi:package-variant" class="inline mr-1" />
              Ver Catálogo
            </router-link>
          </div>

          <!-- Mobile -->
          <router-link 
            :to="`/empresa/${tenantId}/catalogo`" 
            class="md:hidden px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 text-white"
          >
            Catálogo
          </router-link>
        </div>
      </div>
    </nav>

    <!-- Hero -->
    <header class="relative w-full min-h-[60vh] lg:min-h-[70vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden flex justify-center items-center">
      <div class="absolute inset-0 z-0">
        <div class="absolute inset-0 bg-slate-950/40 z-10"></div>
        <div class="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/55 to-slate-950/20 z-10"></div>
        
        <!-- Banner o gradiente por defecto -->
        <img 
          v-if="bannerUrl" 
          :src="bannerUrl" 
          class="w-full h-full object-cover scale-110 opacity-50" 
        />
        <div v-else class="w-full h-full bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900"></div>
      </div>

      <div class="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div class="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span class="text-xs font-semibold text-white/90 tracking-wide">Atención Personalizada</span>
        </div>

        <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
          <span class="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
            {{ config.nombreComercial || config.nombreEmpresa }}
          </span>
        </h1>

        <p v-if="config.slogan" class="text-base sm:text-lg md:text-xl text-slate-200 max-w-2xl mx-auto font-light leading-relaxed mb-8">
          {{ config.slogan }}
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link 
            :to="`/empresa/${tenantId}/catalogo`" 
            class="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
          >
            <Icon icon="mdi:package-variant" class="text-lg" />
            Ver Catálogo de Productos
            <Icon icon="mdi:arrow-right" class="group-hover:translate-x-1 transition-transform" />
          </router-link>

          <a 
            v-if="whatsappNumber"
            :href="whatsappUrl" 
            target="_blank"
            class="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 transition-all"
          >
            <Icon icon="mdi:whatsapp" class="text-lg" />
            Contactar
          </a>
        </div>
      </div>
    </header>

    <!-- Info de contacto -->
    <section class="py-16 px-4 bg-white">
      <div class="max-w-5xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-800 mb-2">Información de Contacto</h2>
          <div class="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full mx-auto"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Dirección -->
          <div v-if="config.direccion || config.ciudad" class="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 text-center hover:shadow-lg transition-all">
            <div class="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:map-marker" class="text-2xl text-white" />
            </div>
            <h3 class="font-bold text-slate-800 mb-2">Dirección</h3>
            <p class="text-sm text-slate-600">
              {{ config.direccion }}
              <span v-if="config.ciudad" class="block">{{ config.ciudad }}</span>
            </p>
          </div>

          <!-- Teléfono -->
          <div v-if="config.telefono1 || config.whatsapp" class="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl p-6 text-center hover:shadow-lg transition-all">
            <div class="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:phone" class="text-2xl text-white" />
            </div>
            <h3 class="font-bold text-slate-800 mb-2">Teléfono</h3>
            <p class="text-sm text-slate-600">
              {{ config.telefono1 }}
              <span v-if="config.telefono2" class="block">{{ config.telefono2 }}</span>
            </p>
          </div>

          <!-- Email -->
          <div v-if="config.email" class="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl p-6 text-center hover:shadow-lg transition-all">
            <div class="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:email" class="text-2xl text-white" />
            </div>
            <h3 class="font-bold text-slate-800 mb-2">Email</h3>
            <p class="text-sm text-slate-600 break-all">{{ config.email }}</p>
          </div>
        </div>

        <!-- Redes sociales -->
        <div v-if="hasSocial" class="mt-12 text-center">
          <h3 class="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Síguenos</h3>
          <div class="flex justify-center gap-4">
            <a v-if="config.instagram" :href="`https://instagram.com/${config.instagram.replace('@','')}`" target="_blank" class="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center text-white hover:scale-110 transition-all">
              <Icon icon="mdi:instagram" class="text-xl" />
            </a>
            <a v-if="config.facebook" :href="`https://facebook.com/${config.facebook}`" target="_blank" class="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white hover:scale-110 transition-all">
              <Icon icon="mdi:facebook" class="text-xl" />
            </a>
            <a v-if="config.tiktok" :href="`https://tiktok.com/@${config.tiktok.replace('@','')}`" target="_blank" class="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white hover:scale-110 transition-all">
              <Icon icon="mdi:music-note" class="text-xl" />
            </a>
            <a v-if="whatsappNumber" :href="whatsappUrl" target="_blank" class="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center text-white hover:scale-110 transition-all">
              <Icon icon="mdi:whatsapp" class="text-xl" />
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Final -->
    <section class="py-16 px-4 bg-gradient-to-br from-slate-900 to-slate-800">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">
          ¿Listo para ver nuestros productos?
        </h2>
        <p class="text-slate-300 mb-8">
          Explora nuestro catálogo completo con los mejores precios y calidad garantizada.
        </p>
        <router-link 
          :to="`/empresa/${tenantId}/catalogo`" 
          class="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl hover:scale-105"
        >
          <Icon icon="mdi:package-variant" class="text-xl" />
          Ir al Catálogo
        </router-link>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-slate-950 text-slate-400 py-8 px-4">
      <div class="max-w-7xl mx-auto text-center">
        <div class="flex items-center justify-center gap-3 mb-4">
          <img 
            :src="logoUrl" 
            :alt="config.nombreComercial" 
            class="h-8 w-auto object-contain rounded opacity-80"
            @error="handleLogoError"
          />
          <span class="font-bold text-white">{{ config.nombreComercial || config.nombreEmpresa }}</span>
        </div>
        <p class="text-xs">
          &copy; {{ currentYear }} {{ config.nombreComercial || config.nombreEmpresa }}. Todos los derechos reservados.
        </p>
        <p class="text-[10px] text-slate-500 mt-2">
          Powered by <span class="font-bold text-slate-400">SYSTEM JJ</span>
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerConfigEmpresa } from "@/api/empresa.js";
import { getAssetUrl } from "@/utils/tenantConfig";

export default {
  name: 'TenantLanding',
  components: { Icon },
  props: {
    tenantId: { 
      type: String, 
      required: true 
    }
  },
  data() {
    return {
      loading: true,
      config: null,
      logoError: false
    };
  },
  computed: {
    logoUrl() {
      if (this.logoError) return this.getFallbackLogo();
      if (this.config?.logoBase64) return this.config.logoBase64;
      if (this.config?.logoUrl) return getAssetUrl(this.config.logoUrl);
      return this.getFallbackLogo();
    },
    bannerUrl() {
      if (this.config?.bannerBase64) return this.config.bannerBase64;
      if (this.config?.bannerUrl) return getAssetUrl(this.config.bannerUrl);
      return null;
    },
    whatsappNumber() {
      return (this.config?.whatsapp || this.config?.telefono1 || '').replace(/\D/g, '');
    },
    whatsappUrl() {
      if (!this.whatsappNumber) return '#';
      const text = encodeURIComponent(`Hola ${this.config?.nombreComercial || ''}, vengo desde su página web.`);
      return `https://wa.me/${this.whatsappNumber}?text=${text}`;
    },
    hasSocial() {
      return !!(this.config?.instagram || this.config?.facebook || this.config?.tiktok || this.whatsappNumber);
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },
  async mounted() {
    await this.cargarConfig();
  },
  methods: {
    getFallbackLogo() {
      // SVG inline con inicial
      const inicial = (this.config?.nombreComercial || this.config?.nombreEmpresa || 'S').charAt(0).toUpperCase();
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <rect width="100" height="100" rx="20" fill="#3b82f6"/>
          <text x="50" y="68" font-size="55" font-weight="bold" fill="white" text-anchor="middle" font-family="Arial">${inicial}</text>
        </svg>
      `)}`;
    },
    async cargarConfig() {
      this.loading = true;
      try {
        const res = await obtenerConfigEmpresa(this.tenantId);
        if (res.success && res.data) {
          this.config = res.data;
          
          // Actualizar título de la página
          document.title = `${this.config.nombreComercial || this.config.nombreEmpresa} - Catálogo`;
          
          // Actualizar favicon
          this.actualizarFavicon();
        } else {
          console.warn('⚠️ Empresa no encontrada:', this.tenantId);
          this.config = null;
        }
      } catch (e) {
        console.error('❌ Error cargando config:', e);
        this.config = null;
      } finally {
        this.loading = false;
      }
    },
    actualizarFavicon() {
      let faviconUrl = null;
      if (this.config?.faviconBase64) {
        faviconUrl = this.config.faviconBase64;
      } else if (this.config?.faviconUrl) {
        faviconUrl = getAssetUrl(this.config.faviconUrl);
      } else if (this.config?.logoBase64) {
        faviconUrl = this.config.logoBase64;
      }
      
      if (!faviconUrl) return;
      
      // Remover favicons existentes
      document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
      
      // Crear nuevo
      const link = document.createElement('link');
      link.rel = 'shortcut icon';
      link.type = 'image/x-icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    },
    handleLogoError() {
      this.logoError = true;
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>